/**
 * Singleton API client for Homepage module
 *
 * Centralizes all API calls with proper typing.
 * Uses Infrastructure's request helper.
 */

import { request } from '../../../Infrastructure'
import { getCategoryUrls, getHomepageConfig, getProductsByCategory, getProductsBySku } from '../queries'

import type { HomepageConfigResponse, MagentoProductsResult } from '../types'

export interface CategoryUrlItem {
  id: number
  name: string
  url_path: string
  url_suffix: string | null
}

export interface CategoryUrlsData {
  categoryList: CategoryUrlItem[]
}

// Type aliases for API responses (for better TypeScript inference)
export interface ProductsData {
  products: MagentoProductsResult
}

/**
 * Homepage API client
 *
 * Note: request<T>() returns Promise<{ data: T }>, so we unwrap to return T directly
 */
export const homepageApi = {
  /**
   * Fetch homepage configuration (banners, sliders, promo blocks)
   */
  getConfig: async (storeId?: number): Promise<HomepageConfigResponse> => {
    const response = await request<HomepageConfigResponse>(
      getHomepageConfig,
      storeId !== undefined ? { storeId } : {}
    )
    return response.data
  },

  /**
   * Fetch products by category ID
   */
  getProductsByCategory: async (categoryId: string, pageSize: number): Promise<ProductsData> => {
    const response = await request<ProductsData>(getProductsByCategory, { categoryId, pageSize })
    return response.data
  },

  /**
   * Fetch products by SKU list
   */
  getProductsBySkus: async (skus: string[], pageSize: number): Promise<ProductsData> => {
    const response = await request<ProductsData>(getProductsBySku, { skus, pageSize })
    return response.data
  },

  /**
   * Fetch category URLs by IDs (batch query)
   */
  getCategoryUrls: async (categoryIds: string[]): Promise<CategoryUrlsData> => {
    const response = await request<CategoryUrlsData>(getCategoryUrls, { categoryIds })
    return response.data
  },
}
